import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import { useAppSelector } from 'hooks/reduxHooks'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import type { ComponentProps } from 'react'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import LoyaltyTierBenefitMessageBanner from './LoyaltyTierBenefitMessageBanner'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import FormatCurrency from 'components/Common/FormatCurrency'

type InheritedLoyaltyTierBenefitMessageBannerProps = Pick<ComponentProps<typeof LoyaltyTierBenefitMessageBanner>, 'flat' | 'icon'>

interface Props extends InheritedLoyaltyTierBenefitMessageBannerProps {
  benefitType: App.LoyaltyBenefitType
  /**
   * @default benefit's availability
   */
  benefitAvailability?: number
}

function LoyaltyTierBenefitAvailabilityMessageBanner({
  benefitType,
  benefitAvailability,
  ...rest
}: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const statusReviewDate = useAppSelector(state => state.loyalty.account.data?.statusReviewDate)
  const benefitView = useLoyaltyUserBenefitView(benefitType)
  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)

  if (!benefitView) return null

  const availability = benefitAvailability ?? benefitView.availability

  if (!availability) return null

  const hasVoucherAmount = (benefitType === 'travelshoot_credit' || benefitType === 'tour_exp_credit')
    && !!benefitView.benefit.value?.amount

  let availabilityMessage: React.ReactNode
  if (benefitType === 'discounted_insurance') {
    availabilityMessage = intl.formatMessage(
      {
        id: 'dsWo+ZHt6r',
        defaultMessage: 'Available to redeem <b>{amount}% off</b>',
        description: 'Loyalty benefit availability for discounted insurance showing percentage discount',
      },
      {
        amount: benefitView.benefit.value?.amount ?? 0,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else if (availability === Infinity) {
    availabilityMessage = intl.formatMessage(
      {
        id: 'wTmOU3FbVk',
        defaultMessage: 'Available to redeem <b>unlimited</b>',
        description: 'Loyalty benefit availability when benefit is unlimited',
      },
      { b: (chunks: React.ReactNode) => <b>{chunks}</b> },
    )
  } else if (hasVoucherAmount && isLeadLoyaltyRegion) {
    const amount = benefitView.benefit.value?.amount ?? 0
    availabilityMessage = intl.formatMessage(
      {
        id: '9Zp6E8i/iW',

        defaultMessage: 'Available to redeem <b>x{count} {amount} {count, plural, one {voucher} other {vouchers}}</b>',
        description: 'Loyalty benefit availability for voucher type in lead region showing count and amount',
      },
      {
        count: availability,
        amount: <FormatCurrency value={amount} format="dynamic" />,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else if (hasVoucherAmount) {
    availabilityMessage = intl.formatMessage(
      {
        id: 'Z0yrtzrAHX',
        defaultMessage: 'Available to redeem <b>x{count, plural, one {# voucher} other {# vouchers}}</b>',
        description: 'Loyalty benefit availability for voucher type in non-lead region showing count',
      },
      {
        count: availability,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  } else {
    availabilityMessage = intl.formatMessage(
      {
        id: 'dK2zRvXjBt',
        defaultMessage: 'Available to redeem <b>x{count}</b>',
        description: 'Loyalty benefit availability showing multiplier count',
      },
      {
        count: <FormatDecimal value={availability} />,
        b: (chunks: React.ReactNode) => <b>{chunks}</b>,
      },
    )
  }

  return <LoyaltyTierBenefitMessageBanner
    tier={displayTier}
    benefitType={benefitType}
    density="compact"
    endContent={!!statusReviewDate && <div>
      <FormattedMessage
        id="RmXB7STXZa"
        defaultMessage="Expires <bold>{date}</bold>"
        description="Expiry date label for loyalty benefit availability"
        values={{
          date: <FormatDateTime value={statusReviewDate} month="short" year="numeric" />,
          bold: (chunks) => <b>{chunks}</b>,
        }}
      />
    </div>}
    {...rest}
  >
    {availabilityMessage}
  </LoyaltyTierBenefitMessageBanner>
}

export default LoyaltyTierBenefitAvailabilityMessageBanner
